import { memo, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';

import InputField from '../../../common/fields/InputField';
import PrimaryButton from '../../../common/buttons/PrimaryButton';
import SectionCard from './SectionCard';

const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: '#F6911D' },
    '&.Mui-focused fieldset': { borderColor: '#F6911D' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#F6911D' },
  "& input[type='time']::-webkit-calendar-picker-indicator": {
    filter: 'invert(0.6)',
  },
  "& input[type='time']:before": {
    content: '""',
    display: 'none',
  },
};

const selectStyles = {
  ...textFieldStyles,
  '& .MuiSelect-select': { color: 'white' },
};

const menuProps = {
  PaperProps: {
    sx: {
      backgroundColor: '#1a1a1a',
      color: 'white',
      '& .MuiMenuItem-root': {
        '&:hover': {
          backgroundColor: 'rgba(246, 145, 29, 0.1)',
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(246, 145, 29, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(246, 145, 29, 0.3)',
          },
        },
      },
    },
  },
};

const TIMEFRAME_OPTIONS = [
  { id: 1, label: '7 Days Review' },
  { id: 2, label: '14 Days Review' },
  { id: 3, label: '1 Month Review' },
  { id: 4, label: '2 Months Review' },
  { id: 5, label: '3 Months Review' },
];

const empty = {
  reviewer_name: '',
  rating: 5,
  comment: '',
  timeframe: TIMEFRAME_OPTIONS[0].id,
};

export default memo(function ReviewsSection({ reviews, onChange }) {
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);

  const handleAdd = () => {
    if (!form.reviewer_name) return;
    onChange([...reviews, form]);
    setForm(empty);
    setShowForm(false);
  };

  const handleRemove = (i) => onChange(reviews.filter((_, idx) => idx !== i));

  return (
    <SectionCard title="Reviews">
      <div className="space-y-3 mb-4">
        {reviews.map((r, i) => (
          <div
            key={i}
            className="flex items-start justify-between bg-white/5 border border-white/10 rounded-lg px-4 py-3"
          >
            <div>
              <p className="text-white text-sm font-medium">{r.reviewer_name}</p>
              <div className="flex mt-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <StarRateRoundedIcon
                    key={s}
                    sx={{ fontSize: 14 }}
                    className={s <= r.rating ? 'text-orange-400' : 'text-gray-600'}
                  />
                ))}
              </div>
              <p className="text-gray-400 text-xs mt-1">{r.comment}</p>
            </div>
            <button type="button" onClick={() => handleRemove(i)}>
              <DeleteForeverOutlinedIcon sx={{ color: '#ef4444', fontSize: 20 }} />
            </button>
          </div>
        ))}
      </div>

      {showForm ? (
        <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3">
          <InputField
            label="Reviewer Name"
            value={form.reviewer_name}
            onChange={(e) => setForm({ ...form, reviewer_name: e.target.value })}
          />
          <div>
            <p className="text-gray-400 text-xs mb-2">Rating</p>
            <div className="flex gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button type="button" key={star} onClick={() => setForm({ ...form, rating: star })}>
                  <StarRateRoundedIcon
                    sx={{ fontSize: 28 }}
                    className={star <= form.rating ? 'text-orange-400' : 'text-gray-600'}
                  />
                </button>
              ))}
            </div>
          </div>
          <FormControl fullWidth sx={selectStyles}>
            <InputLabel>Timeframe</InputLabel>
            <Select
              value={form.timeframe ?? ''}
              onChange={(e) => setForm({ ...form, timeframe: Number(e.target.value) })}
              label="Timeframe"
              MenuProps={menuProps}
            >
              {TIMEFRAME_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <InputField
            label="Comment"
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            multiline
            rows={3}
          />
          <div className="flex flex-col sm:flex-row gap-2 justify-end">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white transition"
            >
              Cancel
            </button>
            <PrimaryButton type="button" onClick={handleAdd}>
              Add Review
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-orange-500/40 text-orange-400 hover:bg-orange-500/10 transition text-sm font-medium"
        >
          <AddCircleOutlineIcon fontSize="small" /> Add Review
        </button>
      )}
    </SectionCard>
  );
});
